import { type AdvancedFiltersPublicState } from '../../../autone-filters';
import { type SelectedGeneralFilters } from '../../general-filters';

/**
 * Counts the total number of selected filters.
 * - For general filters: counts the number of selected options across all filter groups
 * - For advanced filters: counts the number of conditions
 */
export const getSelectedFiltersCount = (
  selectedGeneralFilters: SelectedGeneralFilters | null | undefined,
  selectedAdvancedFilters: AdvancedFiltersPublicState | null | undefined,
): number => {
  const selectedGeneralFiltersCount = selectedGeneralFilters
    ? Object.values(selectedGeneralFilters).reduce((count, options) => {
        return count + Object.values(options).filter(Boolean).length;
      }, 0)
    : 0;

  const selectedAdvancedFiltersCount =
    selectedAdvancedFilters?.conditions?.length ?? 0;

  return selectedGeneralFiltersCount + selectedAdvancedFiltersCount;
};

export const areGeneralFiltersEqual = (
  a: SelectedGeneralFilters,
  b: SelectedGeneralFilters,
) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    const aOptions = a[key] ?? {};
    const bOptions = b[key] ?? {};
    const aOptionKeys = Object.keys(aOptions);
    const bOptionKeys = Object.keys(bOptions);
    if (aOptionKeys.length !== bOptionKeys.length) return false;
    for (const optionKey of aOptionKeys) {
      if (Boolean(aOptions[optionKey]) !== Boolean(bOptions[optionKey])) {
        return false;
      }
    }
  }

  return true;
};

export const areAdvancedFiltersEqual = (
  a: AdvancedFiltersPublicState | null,
  b: AdvancedFiltersPublicState | null,
) => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.conditionCombineMode !== b.conditionCombineMode) return false;
  if (a.conditions.length !== b.conditions.length) return false;

  return a.conditions.every((condition, index) => {
    const other = b.conditions[index];
    if (!other) return false;
    if (
      condition.field !== other.field ||
      condition.operator !== other.operator
    ) {
      return false;
    }
    return JSON.stringify(condition.value) === JSON.stringify(other.value);
  });
};

export const compactSelectedGeneralFilters = (
  filters: SelectedGeneralFilters,
): SelectedGeneralFilters => {
  const cleaned: SelectedGeneralFilters = {};

  for (const [groupId, options] of Object.entries(filters)) {
    const truthyOptions = Object.entries(options).reduce<
      Record<string, boolean>
    >((acc, [optionId, isSelected]) => {
      if (isSelected) {
        acc[optionId] = true;
      }
      return acc;
    }, {});

    if (Object.keys(truthyOptions).length) {
      cleaned[groupId] = truthyOptions;
    }
  }

  return cleaned;
};
